// https://www.typescriptlang.org/play?exactOptionalPropertyTypes=true#code/C4TwDgpgBAKgrmANhAogO2AJxAHhlCAD2AjQBMBnKTCAQzIHs1EQo40BrNBgdzQG0AugBooASQLFSlNp258hUALxQhogErKoaCADcImAHzKAUFFiSS5KjXpMWqgJZoAZgagAJOmVEA6f85umLC0joiCUAD8ZuawCMjoWLgwoYii-P6+YqLsXLxoIlCaAD6qAAYAJADeYvwA5MhoAObAABZ1ggC+ZaJe9ILGAFwx5uomJqCQUADyAEYAVhAAxsCJ2HiW0lRVncZKMfhEVjIMC8vAUSNQVaoA0lDOUBwQIAwusIKDdxoQAI5wjhoZDwhn4t0EEU6-Ger3eMAiRy2D1c7hQl1isTRiOsThRwVuokC7gAahFohiMfdsTIKFhnE0oKU0HAALazdzkikY-iVKq3bqiUlQYZcjE6fTBEWi8XuKVivSyq4yzDjIhgBiYC6TaBrZKbHE7PYHfUyWyMZisfi5eRoPz+a35IRkq7wJCoDDrGBDF0mqjqbz2EAAQUwmFouCJwQAqsZOdzecy2QYBVAoxE5bE5osVrqQarCOrNVAXOwVo4mFBtWQ1o4IBQNtTtrsABSnbPAL4wACUX39dgtIbDuFzXuMVRiNGAcEwaBmZxWvlIdLrrfnwC7UFoVAdfE3foDA9D4ZwI8MhgA3CZOuNtVAAMp05q3F7+pYa6se1gqXOv984WmYPSoizAwDDILQaBniYAD00GqABQFQCBYF0AUN7gNAAByrLspgz4gL+mAfkkWg-ssf6JrhwGgeBkGGDBcE8tUlHJj0SE0ahgjoVMUZoOWaD4YRxHYKRn44EJOB1C4oF1IyUB1LMtCYHU1EoRBUGwaoUkyaptERKU9SKcpumcdx0BZucMAYVo47mLQXwsZgwgxLMkRfAhzTOeYSwOTh7ilOwZAQC4zgQD4MRkG5UAeQyAXkMFoVkFeZlzu2VmQLqolJDgFkrOlED0ZpjjvGQjgULQszIGQki0Cs0xgMA-G0IgAAKmAMJAmogPlVAdY1TAMbE-AAES0MNoiOfpqjDbM43RY+TRTSNSxzY5cmBQlOhkEtw1kHNMVcUV7ykBVVU1XVDVNa17WdaAPVQH1-GDeYI1jRNfmYDts2iAdcnLatH3rfFIVbTte0-QtQNBSDYVcSYb5oLSUAI7SuUXCotmbl8w3SQw40uV8AAsABMXnI18LjNRQEBkwAjF8NzatjjnDVA16dHuyNMLSl4TNZADC3PAGjmXfmJ2pvFziPC2uhWMaNc046Bw1fXNJM7StoiU4g1M7bTc03GagaVhhzMfcN55s3Dt68fxaNaM2MQ3C0UVrRzABkTtQIgwCu4D14bsUXvssAPAQKQXz8I571Jp9bMpbbTAi5+WXrInaBo3L00tADsc7T7ue4V9ECh+HaBzVHH0x0XcMoxcFAMCyEB3iAbJgVoLdt4gzZ1A3Td1F2vPOCQmCU0s0BiC1BgN7OmO0E0EB+7Hl7mGgtBN+5C0r-BjfN63IHhF8AAitAkMlfNTGIGAGGP7okWL2WT9PTBZ69C+FwYO1r03+0Ldb1mDnDKLKAuZAG4EchpRiCYPopkmilPs5oWBgOAWRfsSCjy4GQrRSB5RmIwLYlg0yF9oDp2QSnB+6wwE4DWqUQh6lX7QNjrAwGtCOIQX-lMV0yAyH3xAWJfgMVq4GBMuw1+w0AAMv9ALND1h-OOBlhrEzmnQtC8MhZS1pFw6AKgRq4zVqTa4TwXjY10M1OAEBWadAiFuDRwBea3kFtLLRPCRIUNwBLd4dctFiMkaIJWeNZGiHVn9RRBtqAHgcDCExZiLGWysUAA

type TupleEntry<
	T extends readonly unknown[],
	I extends unknown[] = [],
	R = never,
> = T extends readonly [infer Head, ...infer Tail]
	? TupleEntry<Tail, [...I, unknown], R | [`${I['length']}`, Head]>
	: R;

type ObjectEntry<T extends {}> = T extends object
	? {[K in keyof T]: [K, Required<T>[K]]}[keyof T] extends infer E
		? E extends [infer K, infer V]
			? K extends string | number
				? [`${K}`, V]
				: never
			: never
		: never
	: never;

/**
 * Helper for casting typed `Object.entries`
 *
 * @typeParam T - The infered object type
 *
 * @example
 * const typedEntries = Object.entries({a: 1, b: 2}) as unknown as ReadonlyArray<Entry<{a: number, b: number}>>;
 */
export type Entry<T extends {}> = T extends readonly [unknown, ...unknown[]]
	? TupleEntry<T>
	: T extends ReadonlyArray<infer U>
	? [`${number}`, U]
	: ObjectEntry<T>;

/**
 * This is a typed version of `Object.entries`
 * @typeParam T - The infered object type
 * @param object The object to get entries from
 * @returns A typed array of entries from the object
 *
 * @example
 * ```ts
 * typedEntries(obj).some(([key, value]) => {...});
 * ```
 */
export const typedEntries = <T extends {}>(
	object: T,
): ReadonlyArray<Entry<T>> =>
	Object.entries(object) as unknown as ReadonlyArray<Entry<T>>;
